import React from "react";
import { ArrowRight, Phone, ExternalLink } from "lucide-react";
import { heroSlides, clinicInfo } from "../mock";

export default function Hero() {
  const slide = heroSlides[0];
  return (
    <section id="home" className="relative px-4 md:px-8 pt-8 pb-16 md:pt-12 md:pb-24 bg-[#f0edfa] molecule-bg overflow-hidden">
      <div className="absolute top-32 left-8 w-40 h-40 opacity-40 pointer-events-none">
        <MoleculeSvg />
      </div>
      <div className="absolute bottom-16 right-8 w-56 h-56 opacity-30 pointer-events-none">
        <MoleculeSvg />
      </div>

      <div className="relative mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="fade-up">
          <span className="eyebrow">{slide.tagline}</span>
          <h1 className="section-title mt-2">
            {slide.title}<br />
            <span className="text-[#1a2464]">{slide.titleAccent}</span>
          </h1>
          <div className="h-px bg-gradient-to-r from-[#1a2464]/30 to-transparent my-8 max-w-md" />
          <p className="text-[#4a4a63] max-w-lg text-[15px] md:text-[16px] leading-relaxed">
            {slide.subtitle}
          </p>

          <div className="flex flex-wrap gap-4 mt-8">
            <a
              href={clinicInfo.hotdocUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 bg-[#1a2464] text-white font-semibold pl-6 pr-2 py-2 rounded-full hover:bg-[#141c52] transition-colors"
            >
              <span>Book on HotDoc</span>
              <span className="w-9 h-9 rounded-full bg-[#3dc9b3] grid place-items-center group-hover:translate-x-0.5 transition-transform">
                <ArrowRight className="w-4 h-4 text-white" />
              </span>
              <ExternalLink className="w-3.5 h-3.5 ml-1 text-white/70" />
            </a>
            <a
              href={`tel:${clinicInfo.phoneRaw}`}
              className="pill-btn bg-[#3dc9b3] text-white hover:bg-[#34b39f]"
            >
              <Phone className="w-4 h-4" /> {clinicInfo.phone}
            </a>
          </div>

          {/* Bulk billing badge */}
          <div className="mt-6 inline-flex items-center gap-3 bg-white border border-[#3dc9b3]/30 rounded-full pl-2 pr-5 py-2 shadow-sm">
            <span className="w-8 h-8 rounded-full bg-[#3dc9b3] grid place-items-center">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5 12 10 17 20 7" />
              </svg>
            </span>
            <span className="text-[#1a2464] font-semibold text-sm">
              100% Bulk Billed <span className="text-[#4a4a63] font-normal">with a valid Medicare card</span>
            </span>
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#3dc9b3]/20 blur-2xl" />
          <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] md:aspect-[5/6] max-h-[640px] shadow-xl">
            <img
              src={slide.image}
              alt="Beaudesert General Practice clinic building"
              style={{ objectPosition: "center 55%", filter: "contrast(1.05) saturate(1.08) brightness(1.02)" }}
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2464]/20 via-transparent to-transparent" />
          </div>

          <div className="absolute -bottom-6 left-6 md:left-10 bg-white shadow-lg rounded-2xl px-5 py-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#3dc9b3]/15 grid place-items-center">
              <CheckIcon />
            </div>
            <div>
              <div className="text-[#1a2464] font-bold text-sm">70+ Years</div>
              <div className="text-[#6b6b85] text-xs">Combined experience</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MoleculeSvg() {
  return (
    <svg viewBox="0 0 200 200" fill="none">
      <circle cx="40" cy="40" r="6" fill="#3dc9b3" opacity="0.4" />
      <circle cx="120" cy="60" r="8" fill="#1a2464" opacity="0.15" />
      <circle cx="160" cy="130" r="5" fill="#3dc9b3" opacity="0.35" />
      <circle cx="80" cy="150" r="7" fill="#1a2464" opacity="0.12" />
      <line x1="40" y1="40" x2="120" y2="60" stroke="#1a2464" strokeWidth="1" opacity="0.2" />
      <line x1="120" y1="60" x2="160" y2="130" stroke="#1a2464" strokeWidth="1" opacity="0.2" />
      <line x1="120" y1="60" x2="80" y2="150" stroke="#1a2464" strokeWidth="1" opacity="0.2" />
      <line x1="80" y1="150" x2="160" y2="130" stroke="#1a2464" strokeWidth="1" opacity="0.2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#3dc9b3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5 12 10 17 20 7" />
    </svg>
  );
}
